(() => {
  const main = document.querySelector('#MainContent');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (
    !main ||
    !('IntersectionObserver' in window) ||
    reducedMotion.matches ||
    document.documentElement.classList.contains('shopify-design-mode')
  ) {
    return;
  }

  // Riviera Muse sections: their headings and tiles fade in one by one instead of the whole section at once.
  const RM_HEADS = '.rm-sec__head, .rm-look__head, .rm-service__head';
  const RM_GROUPS =
    '.rm-featured__list, .rm-row, .rm-occasions__grid, .rm-look__track, .rm-service__list, .rm-about__hero, .rm-about__approach-inner, .rm-about__selection, .rm-about__end';
  const STAGGER = 90;
  const MAX_DELAY = 360;

  const clearReveal = (element) => {
    element.removeAttribute('data-scroll-reveal');
    element.removeAttribute('data-scroll-reveal-fade');
    element.style.removeProperty('--scroll-reveal-delay');
  };

  const reveal = (target, revealDelay) => {
    if (revealDelay) target.style.setProperty('--scroll-reveal-delay', `${revealDelay}ms`);
    target.dataset.scrollReveal = 'visible';

    const clearRevealState = (event) => {
      if (event.target !== target || event.propertyName !== 'opacity') return;
      clearReveal(target);
      target.removeEventListener('transitionend', clearRevealState);
    };

    target.addEventListener('transitionend', clearRevealState);
  };

  const revealObserver = new IntersectionObserver(
    (entries) => {
      // Tiles that come into view together are staggered in reading order.
      let batchIndex = 0;

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const target = entry.target;
        revealObserver.unobserve(target);

        if (target.hasAttribute('data-scroll-reveal-group')) {
          // A swipe row: reveal its tiles together, including the ones still off to the side.
          target.removeAttribute('data-scroll-reveal-group');
          Array.from(target.children).forEach((child, index) => {
            if (child.dataset.scrollReveal === 'pending') reveal(child, Math.min(index * STAGGER, MAX_DELAY));
          });
          return;
        }

        if (target.hasAttribute('data-scroll-reveal-stagger')) {
          target.removeAttribute('data-scroll-reveal-stagger');
          reveal(target, Math.min(batchIndex * STAGGER, MAX_DELAY));
          batchIndex += 1;
          return;
        }

        reveal(target);
      });
    },
    {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  const isBelowFold = (target) => target.getBoundingClientRect().top >= window.innerHeight;

  const observeBelowFold = (target, revealDelay = 0) => {
    if (!(target instanceof HTMLElement) || target.hasAttribute('data-scroll-reveal') || !isBelowFold(target)) {
      return;
    }

    if (revealDelay) target.style.setProperty('--scroll-reveal-delay', `${revealDelay}ms`);
    target.dataset.scrollReveal = 'pending';
    revealObserver.observe(target);
  };

  const observeProductCards = (section) => {
    const cards = section.querySelectorAll('.product-grid__item');

    cards.forEach((card, index) => {
      observeBelowFold(card, Math.min(index * 75, 375));
    });
    return cards.length > 0;
  };

  const observeTile = (tile) => {
    if (!(tile instanceof HTMLElement) || tile.hasAttribute('data-scroll-reveal') || !isBelowFold(tile)) return;
    tile.dataset.scrollReveal = 'pending';
    tile.dataset.scrollRevealStagger = '';
    revealObserver.observe(tile);
  };

  const observeRmTiles = (section) => {
    const groups = section.querySelectorAll(RM_GROUPS);
    if (groups.length === 0) return false;

    section.querySelectorAll(RM_HEADS).forEach(observeTile);

    groups.forEach((group) => {
      const tiles = Array.from(group.children).filter((child) => child instanceof HTMLElement);
      const isSwipeRow = getComputedStyle(group).overflowX !== 'visible' && group.scrollWidth > group.clientWidth + 1;

      if (!isSwipeRow) {
        tiles.forEach(observeTile);
        return;
      }

      if (!isBelowFold(group)) return;
      tiles.forEach((tile) => {
        tile.dataset.scrollReveal = 'pending';
        tile.dataset.scrollRevealFade = '';
      });
      group.dataset.scrollRevealGroup = '';
      revealObserver.observe(group);
    });
    return true;
  };

  const scanSections = () => {
    const sections = Array.from(main.children).filter((element) => element.matches('.shopify-section'));

    sections.forEach((section, index) => {
      if (observeProductCards(section)) return;
      if (observeRmTiles(section)) return;
      if (index > 0) observeBelowFold(section);
    });
  };

  scanSections();

  const contentObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches('.product-grid__item')) observeBelowFold(node);
        node.querySelectorAll('.product-grid__item').forEach(observeBelowFold);
      });
    });
  });

  contentObserver.observe(main, { childList: true, subtree: true });

  const resetAll = () => {
    revealObserver.disconnect();
    main.querySelectorAll('[data-scroll-reveal]').forEach(clearReveal);
    main.querySelectorAll('[data-scroll-reveal-group], [data-scroll-reveal-stagger]').forEach((element) => {
      element.removeAttribute('data-scroll-reveal-group');
      element.removeAttribute('data-scroll-reveal-stagger');
    });
  };

  reducedMotion.addEventListener('change', (event) => {
    if (!event.matches) return;
    resetAll();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    resetAll();
    scanSections();
  });
})();
